import { router } from "@inertiajs/react"
import { Plus, Share, Smartphone, SquarePlus } from "lucide-react"
import { type ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useIsMobile } from "@/hooks/use-mobile"
import { usePwaInstall } from "@/hooks/use-pwa-install"
import { homeScreenPromptPath } from "@/routes"

/**
 * AddToHomeScreenDrawer — OKS-668.
 *
 * A one-time, mobile-only "Add OKS Crew to your home screen" tutorial rendered
 * as a bottom `ui/drawer` sheet. Mounted ONCE in `creator-shell.tsx` so it is
 * structurally creator-only — a brand / agency / admin session never renders
 * the creator shell, so this never appears for them.
 *
 * Renders (opens) ONLY when ALL hold (UX Spec §2 / PRD §3):
 *   - role === "creator" (guaranteed by the shell; re-checked defensively),
 *   - mobile viewport (`useIsMobile`, 768px),
 *   - NOT already installed / standalone (`usePwaInstall`),
 *   - NOT yet dismissed server-side (`currentUser.home_screen_prompt_seen`),
 *   - the install hook is `ready` (client signals read — avoids a standalone
 *     flash and drives the slide-up transition).
 * Otherwise it returns `null` (no DOM) — the dominant case for most traffic.
 *
 * Platform branch (UX Spec §1/§3):
 *   - Android/Chromium with a captured `beforeinstallprompt` → a primary
 *     "Add to home screen" CTA that fires the native prompt.
 *   - iOS → illustrated Share → "Add to Home Screen" → Add numbered steps
 *     (+ a "do this from Safari" note on iOS non-Safari browsers).
 *   - Anything else (incl. Android where the prompt never fired) → the generic
 *     illustrated steps (add from the browser menu).
 *
 * Dismiss (Maybe later / Got it / backdrop tap / swipe-down / after invoking
 * install) is OPTIMISTIC + server-persisted (NO localStorage, Crew rule):
 * closing hides the drawer immediately and PATCHes `homeScreenPromptPath()`,
 * which stamps `users.home_screen_prompt_seen_at`. On persist FAILURE we fail
 * safe — the drawer still stays closed locally (the creator is never trapped);
 * the flag simply isn't stamped, so the tutorial may re-show on the next mobile
 * landing (PRD §6 / UX Spec §3 error state). No toast.
 *
 * Brand isolation: reads `currentUser` only; renders zero brand data.
 *
 * Accessibility: `ui/drawer` (Base UI) renders a labelled dialog (title + description) and
 * handles focus trap/restore. The decorative icons are `aria-hidden`; the
 * dismiss control is a real labelled `<button>`.
 */
export function AddToHomeScreenDrawer() {
  const { t } = useTranslation()
  const { currentUser } = useCurrentContext()
  const isMobile = useIsMobile()
  const {
    isReady,
    platform,
    isStandalone,
    isIosNonSafari,
    canPromptInstall,
    promptInstall,
  } = usePwaInstall()
  const [dismissed, setDismissed] = useState(false)

  const eligible =
    isReady &&
    currentUser?.role === "creator" &&
    isMobile &&
    !isStandalone &&
    !currentUser.home_screen_prompt_seen &&
    !dismissed

  if (!eligible) return null

  // Stamp "seen" server-side + collapse locally. Optimistic; fail-safe on error
  // (stay closed, leave unstamped so it may re-show next mobile landing).
  function stampSeen() {
    setDismissed(true)
    router.patch(
      homeScreenPromptPath(),
      {},
      { preserveScroll: true, preserveState: true },
    )
  }

  async function handleInstall() {
    await promptInstall()
    // Accept or dismiss of the OS sheet both mark the tutorial seen (PRD §3).
    stampSeen()
  }

  const showAndroidCta = platform === "android" && canPromptInstall
  const showIosSteps = platform === "ios"

  return (
    <Drawer
      open
      onOpenChange={(next) => {
        // Any close path (backdrop, swipe, Esc) stamps seen.
        if (!next) stampSeen()
      }}
    >
      <DrawerContent data-testid="home-screen-prompt">
        <DrawerHeader>
          <div
            aria-hidden="true"
            className="bg-primary/10 text-primary mx-auto mb-2 flex size-14 items-center justify-center rounded-2xl"
          >
            <Smartphone className="size-7" />
          </div>
          <DrawerTitle>{t("home_screen_prompt.title")}</DrawerTitle>
          <DrawerDescription>
            {t("home_screen_prompt.subtitle")}
          </DrawerDescription>
        </DrawerHeader>

        <div className="overflow-y-auto px-4 pb-2">
          {showAndroidCta ? (
            <Button
              type="button"
              size="lg"
              className="min-h-[44px] w-full"
              onClick={() => void handleInstall()}
              data-testid="home-screen-prompt-android-cta"
            >
              {t("home_screen_prompt.android.cta")}
            </Button>
          ) : showIosSteps ? (
            <ol
              className="flex flex-col gap-3 text-left"
              data-testid="home-screen-prompt-ios-steps"
            >
              <Step
                index={1}
                icon={<Share className="size-5" aria-hidden="true" />}
              >
                {t("home_screen_prompt.ios.step1")}
              </Step>
              <Step
                index={2}
                icon={<SquarePlus className="size-5" aria-hidden="true" />}
              >
                {t("home_screen_prompt.ios.step2")}
              </Step>
              <Step
                index={3}
                icon={<Plus className="size-5" aria-hidden="true" />}
              >
                {t("home_screen_prompt.ios.step3")}
              </Step>
              {isIosNonSafari && (
                <li className="text-muted-foreground text-sm">
                  {t("home_screen_prompt.ios.safari_note")}
                </li>
              )}
            </ol>
          ) : (
            // Generic fallback: Android without a captured prompt, or an
            // unknown browser. Reuse the iOS-style illustrated steps container
            // so the testid stays stable for the "illustrated fallback" case.
            <ol
              className="flex flex-col gap-3 text-left"
              data-testid="home-screen-prompt-ios-steps"
            >
              <Step
                index={1}
                icon={<SquarePlus className="size-5" aria-hidden="true" />}
              >
                {t("home_screen_prompt.generic.step1")}
              </Step>
              <Step
                index={2}
                icon={<Plus className="size-5" aria-hidden="true" />}
              >
                {t("home_screen_prompt.generic.step2")}
              </Step>
            </ol>
          )}
        </div>

        <DrawerFooter>
          <DrawerClose
            render={
              <Button
                type="button"
                variant="ghost"
                className="min-h-[44px] w-full"
                data-testid="home-screen-prompt-dismiss"
              />
            }
          >
            {showAndroidCta
              ? t("home_screen_prompt.dismiss")
              : t("home_screen_prompt.done")}
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

/** A single numbered instruction row with an inline glyph. */
function Step({
  index,
  icon,
  children,
}: {
  index: number
  icon: ReactNode
  children: ReactNode
}) {
  return (
    <li className="flex items-center gap-3">
      <span
        aria-hidden="true"
        className="bg-muted text-foreground flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
      >
        {index}
      </span>
      <span aria-hidden="true" className="text-muted-foreground shrink-0">
        {icon}
      </span>
      <span className="text-sm">{children}</span>
    </li>
  )
}
